import { useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { deviceCalendarService } from '@/lib/calendar/DeviceCalendarService';
import type { CalendarEvent } from '@/types/events';

type UseUpcomingEventResult = {
  events: CalendarEvent[];
  todaysAllDayEvents: CalendarEvent[];
  todaysCompletedEvents: CalendarEvent[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const MAX_UPCOMING_EVENTS = 3;

/**
 * Hook to fetch and manage upcoming calendar events plus today's completed events.
 * Returns three groups:
 * - todaysAllDayEvents: all-day events from today (full opacity)
 * - todaysCompletedEvents: timed events from today that have already started (faded)
 * - events: next 3 future events that haven't happened yet
 */
export function useUpcomingEvent(): UseUpcomingEventResult {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [todaysAllDayEvents, setTodaysAllDayEvents] = useState<CalendarEvent[]>([]);
  const [todaysCompletedEvents, setTodaysCompletedEvents] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const parseEventDateTime = (dateStr: string, timeStr: string | null | undefined): Date => {
    if (!timeStr) {
      return new Date(`${dateStr}T00:00:00`);
    }
    return new Date(`${dateStr}T${timeStr}:00`);
  };

  const compareEvents = (a: CalendarEvent, b: CalendarEvent): number => {
    const dateA = parseEventDateTime(a.startDate, a.startTime);
    const dateB = parseEventDateTime(b.startDate, b.startTime);
    return dateA.getTime() - dateB.getTime();
  };

  /**
   * Fetch events and split into three groups:
   * today's all-day, today's completed timed, and upcoming future events.
   */
  const fetchUpcomingEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { status } = await deviceCalendarService.checkPermissions();

      if (status !== 'granted') {
        const { status: newStatus } = await deviceCalendarService.requestPermissions();

        if (newStatus !== 'granted') {
          setError('Calendar permission not granted');
          setIsLoading(false);
          return;
        }
      }

      let calendarIds: string[] = [];
      try {
        const savedSelection = await AsyncStorage.getItem('selectedCalendarIds');
        if (savedSelection) {
          calendarIds = JSON.parse(savedSelection);
        }
      } catch (storageError) {
        console.warn('[useUpcomingEvent] Error reading calendar IDs from storage:', storageError);
      }

      if (calendarIds.length === 0) {
        const availableCalendars = await deviceCalendarService.getDeviceCalendars();
        calendarIds = availableCalendars.map((cal) => cal.id);
      }

      if (calendarIds.length === 0) {
        setError('No calendars available');
        setIsLoading(false);
        return;
      }

      const now = new Date();
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);

      const todayDateStr = `${todayStart.getFullYear()}-${String(todayStart.getMonth() + 1).padStart(2, '0')}-${String(todayStart.getDate()).padStart(2, '0')}`;

      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 3);

      // Fetch from start of today (not now) so we get today's past events
      const allEvents = await deviceCalendarService.listEvents(calendarIds, todayStart, endDate);

      const allDay: CalendarEvent[] = [];
      const completed: CalendarEvent[] = [];
      const future: CalendarEvent[] = [];

      for (const event of allEvents) {
        const isToday = event.startDate === todayDateStr;

        if (isToday && !event.startTime) {
          allDay.push(event);
        } else if (isToday && event.startTime) {
          const eventDateTime = parseEventDateTime(event.startDate, event.startTime);
          if (eventDateTime < now) {
            completed.push(event);
          } else {
            future.push(event);
          }
        } else {
          const eventDate = new Date(`${event.startDate}T00:00:00`);
          if (eventDate >= todayStart) {
            future.push(event);
          }
        }
      }

      allDay.sort(compareEvents);
      completed.sort(compareEvents);
      future.sort(compareEvents);

      setTodaysAllDayEvents(allDay);
      setTodaysCompletedEvents(completed);
      setEvents(future.slice(0, MAX_UPCOMING_EVENTS));
    } catch (err) {
      console.error('[useUpcomingEvent] Error fetching upcoming events:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch upcoming events');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchUpcomingEvents();
  }, [fetchUpcomingEvents]);

  useEffect(() => {
    fetchUpcomingEvents();
  }, [fetchUpcomingEvents]);

  return {
    events,
    todaysAllDayEvents,
    todaysCompletedEvents,
    isLoading,
    error,
    refresh,
  };
}
